import axios from "axios";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, TextField } from "@mui/material";
import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import { uniqueId } from "lodash";
import NotesTimeline from "../checks/checkNotes/NotesTimeline";
import { UserContext } from "../contexts/UserContext";
import { processedNotes } from "../utils/processNotes";
import { prependUserToSelectNote } from "../utils/prependUserToSelectNote";
import { CloseIcon } from "../../minimals/theme/overrides/CustomIcons";
import { isSurveyZoningStatusNote } from "./orderedDetails";
import { RequestedTaskDescription, SurveyAndZoning } from "./types";

interface Props {
  entity: SurveyAndZoning;
  isOpen: boolean;
  orderNumberDisplay?: string;
  setEntity: Dispatch<SetStateAction<SurveyAndZoning | null>>;
  setRows: Dispatch<SetStateAction<SurveyAndZoning[]>>;
  taskDescriptions: RequestedTaskDescription[];
}

export default function SurveyAndZoningNotesModal({
  entity,
  isOpen,
  orderNumberDisplay,
  setEntity,
  setRows,
  taskDescriptions
}: Props) {
  const { user } = useContext(UserContext);
  const [submitting, setSubmitting] = useState(false);
  const [text, setText] = useState("");

  const primaryTaskDescription = taskDescriptions[0];

  const save = async () => {
    if (!text.trim() || !primaryTaskDescription || !user) return;

    setSubmitting(true);

    try {
      await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
        orderNumber: entity.orderNumber,
        taskDescription: primaryTaskDescription,
        text: prependUserToSelectNote(text.trim(), user.name)
      });

      const optimisticNote = {
        id: parseFloat(uniqueId()),
        uniqueIdentifier: entity.uniqueIdentifier,
        orderNumber: entity.orderNumber,
        taskDescription: primaryTaskDescription,
        createdByUser: user.name,
        createdBy: user.name,
        createdByUserEmail: user.email || "",
        text: text.trim(),
        dateCreated: new Date()
      };

      const updatedEntity = {
        ...entity,
        notes: [...entity.notes, optimisticNote]
      };

      setEntity(updatedEntity);
      setRows((currentRows) =>
        currentRows.map((row) =>
          row.orderNumber === entity.orderNumber
            ? {
                ...row,
                notes: [...row.notes, optimisticNote]
              }
            : row
        )
      );
      setText("");
    } finally {
      setSubmitting(false);
    }
  };

  const onClose = () => setEntity(null);
  const notes = useMemo(
    () => processedNotes((entity.notes || []).filter((note) => !isSurveyZoningStatusNote(note.text)) as any),
    [entity.notes]
  );

  return (
    <Dialog open={isOpen} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center" marginBottom={3}>
          <Box>Order Number {orderNumberDisplay || entity.orderNumber}</Box>
        </Stack>
      </DialogTitle>
      <IconButton
        onClick={onClose}
        disabled={submitting}
        sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ paddingBottom: 3 }}>
        <Box paddingTop={1}>
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            rows={3}
            label="Note"
            sx={{ marginBottom: 2 }}
          />
          <Stack direction="row" justifyContent="right" marginBottom={2}>
            <Button variant="contained" size="small" onClick={save} disabled={submitting || !text.trim()}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
        <NotesTimeline notes={notes} />
      </DialogContent>
    </Dialog>
  );
}
